import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, ChevronDown, ChevronUp, Users, Settings, ArrowLeft, Rocket, Loader2, Clock, Shield, Trash2 } from "lucide-react";
import { toast } from "sonner";
import LeagueForm from "./LeagueForm";
import AdminRescheduleQueue from "./AdminRescheduleQueue";
import AdminDisputeQueue from "./AdminDisputeQueue";
// UPPDRAG-22: AdminTeams was previously a standalone /manager/teams page
// + sidebar entry (PR-12.3). Conceptually it belongs under Serier — moved
// here as a viewMode like reschedules/disputes for consistency.
import AdminTeams from "./AdminTeams";
// TASK-73 Phase 1 PR-B: per-omgång match generation extracted into a
// reusable helper (`generateMatchesForRound`). publishMutation below now
// loops the league's rounds and calls the helper per round (idempotent
// via skipIfMatchesExist=true). Legacy in-line `generateRoundRobin` +
// `pickBallHolder` calls retired with this PR — both helpers are still
// transitively used inside the new orchestrator.
import { generateMatchesForRound, type GenerateMatchesForRoundResult } from "@/lib/leagueScheduling";

type LeagueWithHall = {
  id: string;
  name: string;
  category: string;
  status: string;
  hall_id: string;
  start_date: string | null;
  end_date: string | null;
  registration_deadline: string | null;
  max_teams_per_division: number;
  match_duration_minutes: number;
  created_at: string;
  halls: { name: string } | null;
  teamCount: number;
  // UPPDRAG-51: ball-holder rotation principle. Used by publishMutation
  // to compute league_matches.ball_holder_user_id at insert time. Falls
  // back to 'alternating_pair' (the column DEFAULT) if absent.
  ball_holder_principle?: "alternating_pair" | "by_match_number" | "club_provides";
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  draft: "leagues.statusDraft",
  registration_open: "leagues.statusRegistrationOpen",
  active: "leagues.statusActive",
  completed: "leagues.statusCompleted",
};

const STATUS_COLORS: Record<string, string> = {
  draft: "secondary",
  registration_open: "default",
  active: "default",
  completed: "outline",
};

export default function AdminLeagues() {
  const { t } = useTranslation("admin");
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingLeague, setEditingLeague] = useState<any | null>(null);
  // UPPDRAG-22: added "teams" viewMode (was a standalone /manager/teams page)
  const [viewMode, setViewMode] = useState<"list" | "form" | "reschedules" | "disputes" | "teams">("list");
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishLeague, setPublishLeague] = useState<LeagueWithHall | null>(null);

  // CROSS-ORG-HÄRDNING: explicit .eq("organization_id") på listan + teams-count.
  // Ref: audit-reports/cross-org-data-leakage/rapport.md (sektion 7.1)
  // Ref: audit-reports/fix-remove-superadmin-bypass/rapport.md
  // Detta är belt-and-suspenders ovanpå RLS-härdningen i samma sprint.
  const { data: leagues = [], isLoading } = useQuery({
    queryKey: ["admin-leagues", organization?.id],
    queryFn: async () => {
      const { data: leaguesData, error } = await supabase
        .from("leagues")
        .select("*, halls(name)")
        .eq("organization_id", organization!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const leagueIds = (leaguesData ?? []).map((l) => l.id);
      const { data: teamCounts } = leagueIds.length > 0
        ? await supabase
            .from("teams")
            .select("league_id")
            .in("league_id", leagueIds)
        : { data: [] as { league_id: string }[] };

      const countMap: Record<string, number> = {};
      (teamCounts ?? []).forEach((t) => {
        countMap[t.league_id] = (countMap[t.league_id] || 0) + 1;
      });

      return (leaguesData ?? []).map((l) => ({
        ...l,
        teamCount: countMap[l.id] || 0,
      })) as LeagueWithHall[];
    },
    enabled: !!organization?.id,
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("leagues").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-leagues"] });
      toast.success(t("toast.statusUpdated"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("toast.couldNotUpdate")); },
  });

  const publishMutation = useMutation({
    mutationFn: async (league: LeagueWithHall) => {
      // TASK-73 Phase 1 PR-B: per-omgång generation. publishMutation
      // delegates match-creation to generateMatchesForRound for each
      // league_round. Idempotent via skipIfMatchesExist=true so manually-
      // pre-generated rounds (e.g. via the Omgångar-tab "Generera
      // matcher" button) aren't re-rolled. Phase 5 (future) will add
      // hard-lock + email/push at publish.
      const { data: roundsRaw, error: rndErr } = await supabase
        .from("league_rounds")
        .select("id, round_number")
        .eq("league_id", league.id)
        .order("round_number", { ascending: true });
      if (rndErr) throw rndErr;
      const rounds = (roundsRaw ?? []) as Array<{ id: string; round_number: number }>;

      // Aggregate diagnostics across all rounds for the toast.
      let totalCreated = 0;
      let totalPlaced = 0;
      let totalUnplaced = 0;
      for (const r of rounds) {
        const result: GenerateMatchesForRoundResult = await generateMatchesForRound(supabase, {
          roundId: r.id,
          leagueId: league.id,
          skipIfMatchesExist: true,
        });
        totalCreated += result.matchesCreated;
        totalPlaced += result.matchesPlaced;
        totalUnplaced += result.matchesUnplaced;
      }
      // No-op safety: if league has 0 rounds, publish still flips status
      // (legacy behaviour for a league configured without omgångar). The
      // admin can later define rounds + click "Generera matcher" to
      // populate league_matches.

      const { error: statusErr } = await supabase
        .from("leagues")
        .update({ status: "active" })
        .eq("id", league.id);
      if (statusErr) throw statusErr;

      // TASK-67 PR-D / Phase 1 PR-B: notifications go to all team_members
      // (any role, any user_id) for this league — the helper's bulk
      // INSERT created matches for every roster slot, so the recipient
      // set should match. Re-fetch teams to compute the union without
      // duplicating the helper's roster logic.
      const { data: teamsForNotif } = await supabase
        .from("teams")
        .select("id, members:team_members(user_id)")
        .eq("league_id", league.id);
      const playerIds = new Set<string>();
      for (const t of (teamsForNotif ?? []) as Array<{
        id: string;
        members: Array<{ user_id: string | null }> | null;
      }>) {
        for (const m of t.members ?? []) {
          if (m.user_id) playerIds.add(m.user_id);
        }
      }
      // TASK-75 PR-D V-5: include `action_url` so the notification deep-links
      // straight to the league page when clicked. Notifications.tsx:79-82
      // already navigates to action_url if set; previously this was empty so
      // the click was a no-op.
      const notifications = Array.from(playerIds).map((uid) => ({
        user_id: uid,
        organization_id: organization!.id,
        title: t("leagues.publishedNotifTitle", { name: league.name }),
        body: t("leagues.publishedNotifBody"),
        type: "league_published",
        data: { league_id: league.id },
        action_url: `/leagues/${league.id}`,
      }));
      if (notifications.length > 0) {
        await supabase.from("notifications").insert(notifications);
      }

      return { totalCreated, totalPlaced, totalUnplaced, roundsProcessed: rounds.length };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-leagues"] });
      setPublishOpen(false);
      setPublishLeague(null);
      toast.success(t("leagues.publishSuccess"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("leagues.couldNotPublish")); },
  });

  const createDraftMutation = useMutation({
    mutationFn: async () => {
      // G-031: the org comes from the useOrganization() context — never from
      // "the first organization_members row", which is arbitrary for users who
      // belong to several orgs.
      if (!organization?.id) throw new Error(t("leagues.noOrganizationFound"));

      const { data: hall } = await supabase
        .from("halls")
        .select("id")
        .eq("organization_id", organization.id)
        .limit(1)
        .single();
      if (!hall) throw new Error(t("leagues.noHallFound"));

      // Sprint D PR-AC: explicit currency for cleanliness (per Q2). All money cols
      // default to 0 in DB; defensive trigger trg_dual_write_leagues_minor backfills
      // _minor cols on first INSERT.
      const { data, error } = await supabase.from("leagues").insert({
        name: "Ny serie (utkast)",
        organization_id: organization.id,
        hall_id: hall.id,
        status: "draft",
        category: "Herr",
        created_by: user!.id,
        currency: "SEK",
      }).select("*").single();

      if (error) throw error;
      return data;
    },
    onSuccess: (draft) => {
      queryClient.invalidateQueries({ queryKey: ["admin-leagues"] });
      setEditingLeague(draft);
      setViewMode("form");
      toast.success(t("leagues.draftCreated"));
    },
    onError: (error: Error) => {
      console.error("Draft creation error:", error);
      toast.error(`${t("leagues.couldNotCreateDraft")}: ${error?.message || t("leagues.form.unknownError")}`);
    },
  });

  const deleteDraftMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("leagues").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-leagues"] });
      toast.success(t("leagues.draftDeleted"));
    },
    onError: (error: Error) => toast.error(`${t("toast.couldNotRemove")}: ${error?.message || t("leagues.form.unknownError")}`),
  });

  const filtered = statusFilter === "all" ? leagues : leagues.filter((l) => l.status === statusFilter);

  if (viewMode === "form") {
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={() => { setViewMode("list"); setEditingLeague(null); }} className="mb-4 gap-1">
          <ArrowLeft className="h-4 w-4" /> {t("leagues.backToLeagues")}
        </Button>
        <h2 className="text-lg font-semibold mb-4">
          {editingLeague ? editingLeague.name : t("leagues.createNewLeague")}
        </h2>
        <LeagueForm
          league={editingLeague}
          onSuccess={() => {
            setViewMode("list");
            setEditingLeague(null);
            queryClient.invalidateQueries({ queryKey: ["admin-leagues"] });
          }}
        />
      </div>
    );
  }

  if (viewMode === "reschedules") {
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={() => setViewMode("list")} className="mb-4 gap-1">
          <ArrowLeft className="h-4 w-4" /> {t("leagues.backToLeagues")}
        </Button>
        <h2 className="text-lg font-semibold mb-4">{t("leagues.rescheduleRequests")}</h2>
        <AdminRescheduleQueue />
      </div>
    );
  }

  if (viewMode === "disputes") {
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={() => setViewMode("list")} className="mb-4 gap-1">
          <ArrowLeft className="h-4 w-4" /> {t("leagues.backToLeagues")}
        </Button>
        <h2 className="text-lg font-semibold mb-4">{t("leagues.disputedResults")}</h2>
        <AdminDisputeQueue />
      </div>
    );
  }

  // UPPDRAG-22: Lag (was standalone /manager/teams in PR-12.3, moved here
  // for consistency with reschedules/disputes pattern)
  if (viewMode === "teams") {
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={() => setViewMode("list")} className="mb-4 gap-1">
          <ArrowLeft className="h-4 w-4" /> {t("leagues.backToLeagues")}
        </Button>
        <h2 className="text-lg font-semibold mb-4">{t("leagues.teams", "Lag")}</h2>
        <AdminTeams />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t("leagues.filterStatus")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("leagues.all")}</SelectItem>
            <SelectItem value="draft">{t("leagues.statusDraft")}</SelectItem>
            <SelectItem value="registration_open">{t("leagues.statusRegistrationOpen")}</SelectItem>
            <SelectItem value="active">{t("leagues.statusActive")}</SelectItem>
            <SelectItem value="completed">{t("leagues.statusCompleted")}</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          {/* UPPDRAG-22: Lag tab — was standalone sidebar entry in PR-12.3 */}
          <Button variant="outline" size="sm" onClick={() => setViewMode("teams")} className="gap-1">
            <Users className="h-3.5 w-3.5" /> {t("leagues.teams", "Lag")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setViewMode("reschedules")} className="gap-1">
            <Clock className="h-3.5 w-3.5" /> {t("leagues.reschedules")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setViewMode("disputes")} className="gap-1">
            <Shield className="h-3.5 w-3.5" /> {t("leagues.disputes")}
          </Button>
          <Button
            size="sm"
            onClick={() => createDraftMutation.mutate()}
            disabled={createDraftMutation.isPending}
            className="gap-1"
          >
            {createDraftMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {t("leagues.createNewLeague")}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">{t("leagues.loading")}</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("leagues.noLeaguesFound")}</p>
      ) : (
        filtered.map((league) => {
          const expanded = expandedId === league.id;
          return (
            <Card key={league.id}>
              <CardHeader
                className="cursor-pointer py-3"
                onClick={() => setExpandedId(expanded ? null : league.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-base">{league.name}</CardTitle>
                    <Badge variant={STATUS_COLORS[league.status] as any}>
                      {t(STATUS_LABEL_KEYS[league.status]) ?? league.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{league.halls?.name}</span>
                    <span>·</span>
                    <span>{league.category}</span>
                    <span>·</span>
                    <Users className="h-3.5 w-3.5" />
                    <span>{league.teamCount} {t("leagues.teams")}</span>
                    {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>
              </CardHeader>
              {expanded && (
                <CardContent className="pt-0 space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t("leagues.form.start")}:</span>{" "}
                      {league.start_date ? new Date(league.start_date).toLocaleDateString("sv-SE") : t("leagues.notSet")}
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t("leagues.form.end")}:</span>{" "}
                      {league.end_date ? new Date(league.end_date).toLocaleDateString("sv-SE") : t("leagues.notSet")}
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t("leagues.deadline")}:</span>{" "}
                      {league.registration_deadline
                        ? new Date(league.registration_deadline).toLocaleDateString("sv-SE")
                        : t("leagues.notSet")}
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t("leagues.maxTeamsPerDiv")}:</span>{" "}
                      {league.max_teams_per_division}
                    </div>
                  </div>
                  {league.status === "draft" && (
                    <p className="text-xs text-muted-foreground">
                      Skapad {new Date(league.created_at).toLocaleDateString("sv")}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setEditingLeague(league); setViewMode("form"); }}
                      className="gap-1"
                    >
                      <Settings className="h-3.5 w-3.5" /> {t("leagues.manage")}
                    </Button>

                    {league.status === "draft" && (
                      <Button
                        size="sm"
                        onClick={() => statusMutation.mutate({ id: league.id, status: "registration_open" })}
                      >
                        {t("leagues.openRegistration")}
                      </Button>
                    )}

                    {league.status === "draft" && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="gap-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm(t("leagues.deleteDraftConfirm"))) {
                            deleteDraftMutation.mutate(league.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> {t("leagues.delete")}
                      </Button>
                    )}

                    {league.status === "registration_open" && (
                      <Button
                        size="sm"
                        className="gap-1"
                        onClick={() => { setPublishLeague(league); setPublishOpen(true); }}
                      >
                        <Rocket className="h-3.5 w-3.5" /> {t("leagues.publishLeague")}
                      </Button>
                    )}

                    {league.status === "active" && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => statusMutation.mutate({ id: league.id, status: "completed" })}
                      >
                        {t("leagues.endLeague")}
                      </Button>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })
      )}

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("leagues.publishLeague")}</DialogTitle>
            <DialogDescription>
              {t("leagues.publishConfirmation", { name: publishLeague?.name })}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-1">
            <p><span className="text-muted-foreground">{t("leagues.teamCount")}:</span> {publishLeague?.teamCount ?? 0}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishOpen(false)}>{t("leagues.cancel")}</Button>
            <Button
              onClick={() => publishLeague && publishMutation.mutate(publishLeague)}
              disabled={publishMutation.isPending}
              className="gap-1"
            >
              {publishMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("leagues.publish")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
